import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { Spinner } from './spinner';
import { Progress } from './progress';
import { Skeleton } from './skeleton';

const loadingStateVariants = cva(
  'flex flex-col items-center justify-center gap-4 p-6',
  {
    variants: {
      variant: {
        default: 'text-muted-foreground',
        primary: 'text-primary',
        secondary: 'text-secondary-foreground',
      },
      size: {
        sm: 'p-4 gap-2 text-sm',
        default: 'p-6 gap-4',
        lg: 'p-8 gap-6 text-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface LoadingStateProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof loadingStateVariants> {
  type?: 'spinner' | 'progress' | 'skeleton' | 'dots';
  message?: string;
  description?: string;
  progress?: number;
  showProgress?: boolean;
  skeletonLines?: number;
  skeletonHeight?: string;
}

const LoadingState = React.forwardRef<HTMLDivElement, LoadingStateProps>(
  ({
    className,
    variant,
    size,
    type = 'spinner',
    message,
    description,
    progress,
    showProgress = false,
    skeletonLines = 3,
    skeletonHeight = '20px',
    ...props
  }, ref) => {
    const { t } = useTranslation();

    const defaultMessage = message || t('notifications.info.loading');

    const renderLoadingIndicator = () => {
      switch (type) {
        case 'progress':
          return (
            <div className="w-full max-w-sm space-y-2">
              <Progress 
                value={progress} 
                showValue={showProgress}
                variant={variant === 'primary' ? 'default' : 'default'}
                size={size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'default'}
              />
              {showProgress && progress !== undefined && (
                <div className="text-center text-sm text-muted-foreground">
                  {Math.round(progress)}% {t('notifications.progress.percentage', { percent: '' }).replace('% ', '')}
                </div>
              )}
            </div>
          );
        
        case 'skeleton':
          return (
            <div className="w-full max-w-md space-y-2">
              {Array.from({ length: skeletonLines }).map((_, index) => (
                <Skeleton 
                  key={index} 
                  className="w-full" 
                  style={{ height: skeletonHeight }}
                />
              ))}
            </div>
          );
        
        case 'dots':
          return (
            <div className="flex space-x-1">
              {[0, 1, 2].map((index) => (
                <div
                  key={index}
                  className={cn(
                    'w-2 h-2 rounded-full bg-current animate-pulse',
                    size === 'sm' ? 'w-1.5 h-1.5' : size === 'lg' ? 'w-3 h-3' : 'w-2 h-2'
                  )}
                  style={{
                    animationDelay: `${index * 0.2}s`,
                    animationDuration: '1s',
                  }}
                />
              ))}
            </div>
          );
        
        case 'spinner':
        default:
          return (
            <Spinner 
              size={size === 'sm' ? 'sm' : size === 'lg' ? 'xl' : 'lg'} 
            />
          );
      }
    };

    return (
      <div
        ref={ref}
        className={cn(loadingStateVariants({ variant, size, className }))}
        {...props}
      >
        {renderLoadingIndicator()}
        
        <div className="text-center space-y-1">
          {defaultMessage && (
            <div className="font-medium">{defaultMessage}</div>
          )}
          {description && (
            <div className="text-sm text-muted-foreground">{description}</div>
          )}
        </div>
      </div>
    );
  }
);
LoadingState.displayName = 'LoadingState';

// Specialized loading components for common use cases
export const TableLoadingState: React.FC<{ rows?: number }> = ({ rows = 5 }) => {
  return (
    <div className="space-y-3 p-4">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center space-x-4">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
};

export const CardLoadingState: React.FC<{ cards?: number }> = ({ cards = 3 }) => {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: cards }).map((_, index) => (
        <div key={index} className="p-4 border rounded-lg space-y-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
          <div className="flex justify-between">
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-8 w-20" />
          </div>
        </div>
      ))}
    </div>
  );
};

export const FormLoadingState: React.FC = () => {
  return (
    <div className="space-y-6 p-4">
      <div className="space-y-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-20 w-full" />
      </div>
      <div className="flex justify-end space-x-2">
        <Skeleton className="h-10 w-20" />
        <Skeleton className="h-10 w-16" />
      </div>
    </div>
  );
};

export { LoadingState, loadingStateVariants };